import { useEffect, useRef, useState, ReactNode, useCallback } from 'react';
import { Box, Flex, useColorModeValue } from '@chakra-ui/react';

type Props = {
  open: boolean;
  initialPosition: 'bottom' | 'middle' | 'top';
  onClose?: () => void;
  children: ReactNode;
};

const SNAP_POINT_VIEWPORT_RATIOS = [0.09, 0.45, 0.88]; // bottom / middle / top as viewport ratios
const HANDLE_HEIGHT = 28;
const MAX_DRAWER_HEIGHT_VIEWPORT_RATIO = 0.95;

export function MobileDrawer({ open, initialPosition, children }: Props) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const dragInfoRef = useRef({ y0: 0, h0: 0 }); // Stores initial pointer Y and drawer height on drag start
  const [drawerHeight, setDrawerHeight] = useState(0); // Drawer height in pixels
  const [currentSnapIndex, setCurrentSnapIndex] = useState(0); // 0: bottom, 1: middle, 2: top. Default to bottom.

  const backgroundColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Snap points in pixels – recomputed on resize
  const snapPixelValuesRef = useRef<number[]>([]);
  const calculateSnapPixelValues = useCallback(
    () =>
      (snapPixelValuesRef.current = SNAP_POINT_VIEWPORT_RATIOS.map(
        (r) => r * window.innerHeight
      )),
    []
  );

  useEffect(() => {
    const onResize = () => {
      calculateSnapPixelValues();
      // Update height based on the current logical snap index
      if (
        snapPixelValuesRef.current &&
        snapPixelValuesRef.current.length > currentSnapIndex
      ) {
        setDrawerHeight(snapPixelValuesRef.current[currentSnapIndex]);
      } else if (
        snapPixelValuesRef.current &&
        snapPixelValuesRef.current.length > 1
      ) {
        // Fallback to middle
        setDrawerHeight(snapPixelValuesRef.current[1]);
      } else if (
        snapPixelValuesRef.current &&
        snapPixelValuesRef.current.length > 0
      ) {
        // Fallback to first if only one
        setDrawerHeight(snapPixelValuesRef.current[0]);
      }
    };

    onResize(); // Initial calculation
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [calculateSnapPixelValues, currentSnapIndex]);

  // When the drawer opens, snap to the initial position and set snap index
  useEffect(() => {
    if (open) {
      calculateSnapPixelValues(); // Ensure snaps are calculated
      const initialSnapIndex =
        initialPosition === 'bottom' ? 0 : initialPosition === 'middle' ? 1 : 2;
      // Clamp initialSnapIndex to be within bounds of available snap points
      const initialSnapIdx = Math.min(
        initialSnapIndex,
        snapPixelValuesRef.current.length - 1
      );

      if (
        initialSnapIdx >= 0 &&
        snapPixelValuesRef.current.length > initialSnapIdx
      ) {
        setDrawerHeight(snapPixelValuesRef.current[initialSnapIdx]);
        setCurrentSnapIndex(initialSnapIdx);
      }
    } else {
      // Optionally reset to a closed height or a default snap index when closed
      // For now, height is managed by `if(!open) return null;` effectively.
    }
  }, [open, calculateSnapPixelValues, initialPosition]);

  // Helper to clamp height within valid bounds
  const clampHeight = useCallback(
    (v: number) =>
      Math.max(
        HANDLE_HEIGHT,
        Math.min(v, MAX_DRAWER_HEIGHT_VIEWPORT_RATIO * window.innerHeight)
      ),
    []
  ); // Depends on constants only, and live window.innerHeight read

  const commonPointerUpLogic = useCallback((currentHeightPx: number) => {
    const calculatedSnaps = snapPixelValuesRef.current;
    if (calculatedSnaps.length === 0) return;

    const closestSnapIdx = calculatedSnaps.reduce(
      (bestIndex, snapValue, currentIndex, arr) =>
        Math.abs(snapValue - currentHeightPx) <
        Math.abs(arr[bestIndex] - currentHeightPx)
          ? currentIndex
          : bestIndex,
      0
    );
    const targetSnapHeightPx = calculatedSnaps[closestSnapIdx];

    if (drawerRef.current) {
      drawerRef.current.style.transition = 'height 0.25s ease';
      drawerRef.current.style.height = `${targetSnapHeightPx}px`;
    }
    setDrawerHeight(targetSnapHeightPx);
    setCurrentSnapIndex(closestSnapIdx);
  }, []);

  // Pointer down handler for the main drag handle
  const onPointerDownOnHandle = useCallback(
    (e: React.PointerEvent) => {
      if (!drawerRef.current) return;
      drawerRef.current.style.transition = 'none';
      const h0Local = drawerRef.current.offsetHeight;
      const y0Local = e.clientY;
      dragInfoRef.current = { y0: y0Local, h0: h0Local };

      const localMoveHandler = (ev: PointerEvent) => {
        if (!drawerRef.current) return;
        const newHeight = clampHeight(h0Local - (ev.clientY - y0Local));
        drawerRef.current.style.height = `${newHeight}px`;
      };

      const localUpHandler = () => {
        window.removeEventListener('pointermove', localMoveHandler);
        if (drawerRef.current) {
          commonPointerUpLogic(drawerRef.current.offsetHeight);
        }
      };

      window.addEventListener('pointermove', localMoveHandler);
      window.addEventListener('pointerup', localUpHandler, { once: true });
      e.preventDefault();
    },
    [clampHeight, commonPointerUpLogic]
  );

  // Pointer down handler for the content area (active only when at initial position)
  const onPointerDownOnContent = useCallback(
    (e: React.PointerEvent) => {
      if (currentSnapIndex !== 0 || !drawerRef.current) {
        return; // Only allow content drag if drawer is at the initial position
      }
      // If event target is interactive element inside content, allow default behavior.
      // This is a simple check; more robust might involve checking e.target.closest for specific selectors.
      const targetElement = e.target as HTMLElement;
      if (
        targetElement.hasAttribute('data-interactive') ||
        targetElement.closest(
          '[data-interactive], a, button, input, textarea, select'
        )
      ) {
        return;
      }

      drawerRef.current.style.transition = 'none';
      const h0Local = drawerRef.current.offsetHeight;
      const y0Local = e.clientY;
      dragInfoRef.current = { y0: y0Local, h0: h0Local };

      const localMoveHandler = (ev: PointerEvent) => {
        if (!drawerRef.current) return;
        const newHeight = clampHeight(h0Local - (ev.clientY - y0Local));
        drawerRef.current.style.height = `${newHeight}px`;
      };

      const localUpHandler = () => {
        window.removeEventListener('pointermove', localMoveHandler);
        if (drawerRef.current) {
          commonPointerUpLogic(drawerRef.current.offsetHeight);
        }
      };

      window.addEventListener('pointermove', localMoveHandler);
      window.addEventListener('pointerup', localUpHandler, { once: true });
      e.preventDefault(); // Prevent default content scroll if we are dragging the drawer
    },
    [currentSnapIndex, clampHeight, commonPointerUpLogic]
  );

  // --- Render ---
  if (!open) return null;

  return (
    <Box // Overlay
      pos="fixed"
      inset="0"
      zIndex="overlay"
      pointerEvents="none" // Allow interactions with elements behind the overlay
    >
      <Box // Drawer container
        ref={drawerRef}
        position="absolute"
        bottom="0"
        insetX={2}
        height={`${drawerHeight}px`}
        backgroundColor={backgroundColor}
        borderTopRadius="3xl"
        overflow="hidden"
        pointerEvents="auto"
        borderColor={borderColor}
        borderWidth="1px"
        borderBottom="none"
      >
        <Flex direction="column" h="100%">
          {/* Handle */}
          <Flex
            h={`${HANDLE_HEIGHT}px`}
            align="center"
            justify="center"
            cursor="grab"
            style={{ touchAction: 'none' }} // Prevents page scroll on touch devices
            onPointerDown={onPointerDownOnHandle} // Use specific handler for the handle
            userSelect="none"
          >
            <Box w="40px" h="4px" bg={borderColor} borderRadius="2px" />
          </Flex>

          {/* Content */}
          <Box
            flex="1"
            overflow="auto"
            // Conditionally set pointer down and styles for the content area
            onPointerDown={
              currentSnapIndex === 0 ? onPointerDownOnContent : undefined
            }
            style={{
              cursor: currentSnapIndex === 0 ? 'grab' : 'auto',
              touchAction: currentSnapIndex === 0 ? 'none' : 'auto',
            }}
          >
            {children}
          </Box>
        </Flex>
      </Box>
    </Box>
  );
}
